/**
 * Bridges MSAL into the app: mirrors MSAL's account state into the auth slice and exposes
 * the two actions the UI needs.
 *
 * What the context publishes is derived from MSAL during render, not read back from the
 * slice: the mirror is written in an effect, which lands a frame later than the gate in
 * `App` reads it. The slice stays the read-model for anything that wants the account
 * without an MSAL dependency.
 *
 * Ported from the V2 portal's `contexts/AuthContextRedux.tsx`, reduced to the sign-in
 * flow. V2's account-creation and government-identity verification — the verified official
 * e-mail address, the outside-counsel flags, the v1 routing check — are not part of this
 * slice and are intentionally absent.
 *
 * The profile is read from the ID token claims rather than Microsoft Graph, so sign-in
 * needs no `User.Read` consent and no app-registration change beyond the SPA reply URL.
 */
import { type ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import type { AccountInfo } from '@azure/msal-browser';
import { useAppDispatch, useAppSelector } from '@/store';
import { expectedTenantId, loginRequest } from '@/config/authConfig';
import { isTenantAdmitted, toAuthAccount, toUserProfile } from '../accountRules';
import { describeAuthError } from '../authErrors';
import {
  setAccount,
  setAuthError,
  setLoading,
  setTenantBlocked,
  setUserProfile,
} from '../store/authSlice';
import { AuthContext, type AuthContextValue } from './AuthContext';

export function AuthProvider({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const { instance, accounts, inProgress } = useMsal();
  const authState = useAppSelector((state) => state.auth);

  const account = useMemo<AccountInfo | null>(
    () => instance.getActiveAccount() ?? accounts[0] ?? null,
    [instance, accounts],
  );

  // Backstop for the authority: an account whose tenant is not the one this flavor admits
  // — TESTTRS in NPE, the consumer tenant in PPE / PROD — must not be treated as signed
  // in, however it reached the cache. The rule itself is in ../accountRules.
  const tenantBlocked = useMemo(
    () => (account ? !isTenantAdmitted(account, expectedTenantId) : false),
    [account],
  );

  // Derived in render, not read back from the store: the mirror below lands an effect
  // later, and `App` gates on `isAuthenticated`, so reading the store here would paint the
  // sign-in page for one frame on every reload that restores a cached account. `instance`
  // already holds the right account on the first render — `main.tsx` sets it as active
  // before anything mounts.
  const admitted = useMemo(
    // A blocked account is reported as blocked, never as an account: the app must not
    // render a signed-in shell for an identity it has rejected.
    () => (account && !tenantBlocked ? account : null),
    [account, tenantBlocked],
  );

  const userProfile = useMemo(() => (admitted ? toUserProfile(admitted) : null), [admitted]);

  const login = useCallback(async () => {
    try {
      // Clear any earlier failure as the new attempt starts, so a stale message is not
      // still on screen while the user is being redirected.
      dispatch(setAuthError(null));
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      // Reported through the slice, not rethrown: every call site is `void login()`, so a
      // rethrow became an unhandled rejection and the user saw nothing at all.
      console.error('Login failed:', error);
      dispatch(setAuthError(describeAuthError(error)));
    }
  }, [instance, dispatch]);

  const logout = useCallback(async () => {
    try {
      // MSAL clears its cache and navigates away; the mirrored state follows from the
      // account disappearing. Resetting first would only be undone by that mirror before
      // the redirect lands.
      await instance.logoutRedirect();
    } catch (error) {
      console.error('Logout failed:', error);
      dispatch(setAuthError(describeAuthError(error)));
    }
  }, [instance, dispatch]);

  useEffect(() => {
    dispatch(setLoading(inProgress !== 'none'));
  }, [inProgress, dispatch]);

  useEffect(() => {
    dispatch(setTenantBlocked(tenantBlocked));
    // Narrowed on the way in: the store keeps the four identifying fields, not the whole
    // `AccountInfo` with its token claims. The context below still publishes the full
    // account for anything that needs MSAL's own shape.
    dispatch(setAccount(admitted ? toAuthAccount(admitted) : null));
    dispatch(setUserProfile(userProfile));
  }, [admitted, userProfile, tenantBlocked, dispatch]);

  const value: AuthContextValue = {
    isAuthenticated: admitted !== null,
    isLoading: authState.isLoading,
    account: admitted,
    userProfile,
    tenantBlocked,
    // Read from the slice rather than derived: this one has no MSAL counterpart to derive
    // from — it is written by the bootstrap catch in main.tsx and by `login` above.
    authError: authState.authError,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
